import type { ClientListFacets, ClientListItem, ClientSortField } from "@closer/shared";
import {
  CLIENT_SORT_FIELD_NAMES,
  CLIENT_SORT_FIELDS,
  CLIENT_STATUS_NAMES,
  CLIENT_TYPE_NAMES,
} from "@closer/shared";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatTile,
} from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { ClientsIcon } from "../../components/icons";
import { StateView } from "../../components/StateView";
import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { AiClientInsightSection } from "./AiClientInsightSection";
import { ClientCreateModal } from "./ClientCreateModal";

/**
 * MÜVEKKİLLER listesi (CLOS-169 `[116]`).
 *
 * ### ⚠ Süzgeç SUNUCUDA
 *
 * Ekran hiçbir eleme yapmıyor: arşiv ayrımını istemcide yapsaydık sunucu arşivli kayıtları
 * yine gönderirdi ve arşivlenmiş bir müvekkilin varlığı ağ trafiğinden okunabilirdi. Kart:
 * *"erişim dışı müvekkilin adı, sayacı, snippet'i veya VARLIĞI sızmaz."*
 *
 * ### ⚠ Süzgeç durumu ADRESTE, çalışma alanı DAMGASIYLA
 *
 * Bir hukukçu "arşivdeki kuruluşlar" görünümünü meslektaşına bağlantı olarak
 * gönderebilmeli. Ama süzgeç değerleri ALANA ÖZGÜ: başka bir büroya geçildiğinde adreste
 * kalan `createdBy` o alanda kimseye karşılık gelmez ve kullanıcı sebebini göremediği boş
 * bir liste görürdü. Bu yüzden adres `alan` damgası taşıyor ve geçişte temizleniyor
 * (`[084]` ile aynı karar).
 *
 * ### ⚠ Liste bir YETKİ DEĞİL
 *
 * Kart: *"müvekkil listesinde görünmek sözleşme, özel not veya tüm müvekkil verisine
 * erişim vermez."* Satır detaya bağlanıyor (`[117]`) ama bağlantı bir yetki değil: detay
 * ucu tenant, permission ve kaydın kendi kurallarını YENİDEN uyguluyor. Liste ile detay
 * arasında erişim değişebilir; ekran bunu bir hata değil olağan bir sonuç olarak
 * karşılıyor.
 */

const LIMIT = 20;

const ARSIV = "archived";

/** Adreste taşınan süzgeç anahtarları — "temizle" ve alan geçişi bu listeyi siliyor. */
const SUZGEC_ANAHTARLARI = [
  "q",
  "clientType",
  "status",
  "assigneeUserId",
  "sort",
  "dir",
  "page",
] as const;
type SuzgecAnahtari = (typeof SUZGEC_ANAHTARLARI)[number];

function siralamaAlani(ham: string | null): ClientSortField {
  return (CLIENT_SORT_FIELDS as readonly string[]).includes(ham ?? "")
    ? (ham as ClientSortField)
    : "displayName";
}

export function ClientsPage() {
  const { activeTenantId, can } = useAuth();
  const [olusturAcik, setOlusturAcik] = useState(false);
  /** Ayrıntılı süzgeçler, liste ilk açıldığında sonuçları aşağı itmez. */
  const [suzgeclerAcik, setSuzgeclerAcik] = useState(false);
  /** Rota `AppLayout` altında: aktif çalışma alanı olmadan buraya gelinmiyor. */
  const tenantId = activeTenantId!;
  const [params, setParams] = useSearchParams();

  /**
   * ⚠ Temizlik efekt içinde olduğu için o kare boyunca süzgeçler ZATEN YOK sayılıyor.
   * Yalnız efekte güvenseydik, geçişin ilk karesinde yeni alan eski alanın süzgeciyle
   * sorgulanırdı.
   */
  const alanUyusuyor = params.get("alan") === tenantId;

  useEffect(() => {
    if (alanUyusuyor) return;
    const temiz = new URLSearchParams(window.location.search);
    for (const anahtar of SUZGEC_ANAHTARLARI) temiz.delete(anahtar);
    temiz.set("alan", tenantId);
    setParams(temiz, { replace: true });
  }, [alanUyusuyor, tenantId, setParams]);

  function oku(ad: SuzgecAnahtari): string {
    return alanUyusuyor ? (params.get(ad) ?? "") : "";
  }

  const page = Number(oku("page") || "1") || 1;
  const q = oku("q");
  const clientType = oku("clientType");
  const status = oku("status");
  const assigneeUserId = oku("assigneeUserId");
  const sort = siralamaAlani(oku("sort"));
  const dir = oku("dir") === "desc" ? "desc" : "asc";

  const uygulananlar = [q, clientType, status, assigneeUserId].filter(Boolean).length;

  /** Süzgeç değişince İLK SAYFAYA: 3. sayfada kalmak boş bir liste gösterirdi. */
  function suzgecYaz(degisiklikler: Partial<Record<SuzgecAnahtari, string>>) {
    const sonraki = new URLSearchParams(params);
    for (const [anahtar, deger] of Object.entries(degisiklikler)) {
      if (deger) sonraki.set(anahtar, deger);
      else sonraki.delete(anahtar);
    }
    if (!("page" in degisiklikler)) sonraki.delete("page");
    setParams(sonraki);
  }

  function temizle() {
    const sonraki = new URLSearchParams(params);
    for (const anahtar of SUZGEC_ANAHTARLARI) sonraki.delete(anahtar);
    setParams(sonraki);
  }

  function aramaGonder(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const veri = new FormData(e.currentTarget);
    suzgecYaz({ q: String(veri.get("q") ?? "").trim() });
  }

  const sorgu = useQuery({
    queryKey: tenantKey(
      tenantId,
      "clients",
      page,
      q,
      clientType,
      status,
      assigneeUserId,
      sort,
      dir,
    ),
    queryFn: async () => {
      const adres = new URLSearchParams({ page: String(page), limit: String(LIMIT), sort, dir });
      for (const [ad, deger] of Object.entries({ q, clientType, status, assigneeUserId })) {
        if (deger) adres.set(ad, deger);
      }
      return api<{ items: ClientListItem[]; facets: ClientListFacets }>(`/api/clients?${adres}`, {
        tenantId,
      });
    },
  });

  /**
   * SORUMLU süzgecinin seçenekleri (CLOS-247 `[135]`).
   *
   * ⚠ Tür ve durum seçenekleri SAYAÇTAN geliyor; sorumlu öyle olamaz: sayaç yalnız o an
   * listede görünen kayıtları sayar, oysa "kimseye atanmamış" bir alanda süzgeç boş
   * görünür ve kullanıcı hiç kimseyi seçemezdi. Üye dizini bu yüzden ayrı okunuyor.
   *
   * ⚠ Uç `organization.member` istiyor. Yetkisi olmayan kullanıcıda liste BOŞ döner ve boş
   * liste iki farklı şeyin cevabı olabilir — `UyeListesiDurumu` "sorulamadı" ile "kimse
   * yok"u ayırıyor (erişimsizlik yokluk değildir).
   */
  const uyeler = useQuery({
    enabled: can("organization.member"),
    queryKey: tenantKey(tenantId, "members"),
    queryFn: async () =>
      (
        await api<{ members: { userId: string; name: string; status: string }[] }>(
          `/api/tenants/${tenantId}/members`,
          { tenantId },
        )
      ).data.members,
  });
  const aktifUyeler = (uyeler.data ?? []).filter((u) => u.status === "active");

  const facets = sorgu.data?.data.facets;
  const toplam = sorgu.data?.meta?.total ?? 0;
  const sonSayfa = Math.max(1, Math.ceil(toplam / LIMIT));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Müvekkiller"
        description="Hukuk büronuzun müvekkil portföyünü görüntüleyin ve yönetin."
        actions={
          /*
            Kayıt açma eylemi CLOS-627 ile geldi. `[115]` sunucu tarafını teslim etmişti ama
            yüzeyi yoktu; ilk müvekkil konsoldan açılıyordu. Eylem yetkiye bağlı: yetkisi
            olmayan kullanıcıya gösterip 403 duvarına toslatmıyoruz.
          */
          can("client.create") ? (
            <Button type="button" onClick={() => setOlusturAcik(true)}>
              Yeni Müvekkil
            </Button>
          ) : null
        }
      />

      {/*
        AI PORTFÖY İÇGÖRÜLERİ (CLOS-289 `[193]`) — listenin üstünde, ondan ayrı bir kart.
        Birleştirme/temsil kararları listenin kendi kanonik akışlarında kalıyor.
      */}
      <AiClientInsightSection />

      {/**
       * ⚠ TEK sayaç kutusu — spec §7.8 dört tile istiyor (Toplam Müvekkil / Aktif Hizmet
       * İlişkisi / Toplam Sözleşme / Yaklaşan Kritik Tarih) ama `ClientListItem` yalnız
       * `displayName/email/phone/clientType/status/createdByName/createdAt` taşıyor
       * (`packages/shared/src/schemas/client.ts` — kimlik/vergi bilinçli olarak da YOK).
       * Diğer üçünü besleyecek veri YOK; "türetilemeyen tile ÇİZİLMEZ" kuralı gereği
       * yalnız gerçekten türetilebilen (`toplam`) çiziliyor.
       */}
      <StatTile
        icon={<ClientsIcon />}
        value={toplam}
        label="Toplam Müvekkil"
        className="max-w-xs"
      />

      <div className="flex flex-wrap items-center gap-2">
        <form
          onSubmit={aramaGonder}
          className="flex min-w-0 flex-1 items-center gap-2"
          role="search"
        >
          {/*
          ⚠ Kutu ADRESTEKİ değerle yeniden kuruluyor (`key`): "temizle" ya da alan geçişi
          adresi sildiğinde kutuda eski metnin kalması, kullanıcıya uygulanmayan bir süzgeç
          gösterirdi.
        */}
          <Input
            key={q}
            name="q"
            defaultValue={q}
            type="search"
            aria-label="Ad veya e-postada ara"
            placeholder="Müvekkil adı veya e-posta ara…"
            className="min-w-0 flex-1"
          />
          <Button type="submit" variant="secondary">
            Ara
          </Button>
        </form>
        <Button
          type="button"
          variant="secondary"
          aria-expanded={suzgeclerAcik}
          onClick={() => setSuzgeclerAcik((acik) => !acik)}
        >
          Süzgeçler{uygulananlar > 0 ? ` (${uygulananlar})` : ""}
        </Button>
      </div>

      <div
        className={`${suzgeclerAcik ? "grid" : "hidden"} grid-cols-1 gap-4 rounded-lg border border-border-default bg-surface p-5 sm:grid-cols-2 lg:grid-cols-4`}
      >
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-tur">
            Tür
          </label>
          <Select
            id="suzgec-tur"
            value={clientType}
            onChange={(e) => suzgecYaz({ clientType: e.target.value })}
          >
            <option value="">Tümü</option>
            {/*
              ⚠ Seçenekler SAYAÇTAN: sabit bir liste, hiçbir müvekkili görmeyen kullanıcıya
              bile büroda hangi türlerin kullanıldığını söylerdi.
            */}
            {(facets?.clientType ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.count})
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-sorumlu">
            Sorumlu
          </label>
          <Select
            id="suzgec-sorumlu"
            value={assigneeUserId}
            onChange={(e) => suzgecYaz({ assigneeUserId: e.target.value })}
          >
            <option value="">Tümü</option>
            {aktifUyeler.map((u) => (
              <option key={u.userId} value={u.userId}>
                {u.name}
              </option>
            ))}
          </Select>
          {/*
            ⚠ Boş liste iki farklı şeyin cevabı olabilir: yetkisi olmayan kullanıcıda üye
            dizini SORULAMIYOR. "Kimse yok" demek, olmayan bir gerçeği bildirmek olurdu.
          */}
          {can("organization.member") ? (
            <UyeListesiDurumu hata={uyeler.error} bosMu={aktifUyeler.length === 0} />
          ) : (
            <span className="text-xs text-fg-muted">
              Sorumluya göre süzmek için organizasyon üyelerini görme yetkisi gerekiyor.
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-durum">
            Durum
          </label>
          <Select
            id="suzgec-durum"
            value={status}
            onChange={(e) => suzgecYaz({ status: e.target.value })}
          >
            <option value="">Arşiv hariç tümü</option>
            {(facets?.status ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label} ({f.count})
              </option>
            ))}
            {/*
              ⚠ ARŞİV her zaman seçilebilir olmalı. Seçenekler yalnız sayaçtan gelseydi arşiv
              hiç listelenmezdi — varsayılan liste onu zaten hariç tutuyor — ve kullanıcı
              arşive arayüzden ASLA ulaşamazdı. SAYI TAŞIMIYOR: sayı, kullanıcının henüz
              görmediği kayıtların varlığını ele verirdi.
            */}
            {!(facets?.status ?? []).some((f) => f.value === ARSIV) && (
              <option value={ARSIV}>{CLIENT_STATUS_NAMES.archived}</option>
            )}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-fg-strong" htmlFor="suzgec-siralama">
            Sıralama
          </label>
          <Select
            id="suzgec-siralama"
            value={sort}
            onChange={(e) => suzgecYaz({ sort: e.target.value })}
          >
            {CLIENT_SORT_FIELDS.map((alan) => (
              <option key={alan} value={alan}>
                {CLIENT_SORT_FIELD_NAMES[alan]}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex items-end gap-2">
          <Select aria-label="Yön" value={dir} onChange={(e) => suzgecYaz({ dir: e.target.value })}>
            <option value="asc">Artan</option>
            <option value="desc">Azalan</option>
          </Select>
          <Button type="button" variant="ghost" onClick={temizle} disabled={uygulananlar === 0}>
            Temizle
          </Button>
        </div>
      </div>

      {/*
        ⚠ TAZELEME DURUMU ayrı: süzgeç değişince önceki sonuçlar ekranda kalıyor ve
        kullanıcı bir an için ESKİ kümeye bakıyor. Sessiz geçseydi, gelmiş sanılan bir
        sonuç üzerinden karar verilirdi. `aria-live` ile ekran okuyucuya da bildiriliyor.
      */}
      {sorgu.isFetching && !sorgu.isPending && (
        <p role="status" aria-live="polite" className="text-xs text-fg-muted">
          Sonuçlar güncelleniyor…
        </p>
      )}

      {sorgu.isPending ? (
        <div role="status" aria-label="Müvekkiller yükleniyor">
          <Skeleton lines={5} />
        </div>
      ) : sorgu.error || !sorgu.data ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data.data.items.length === 0 ? (
        <EmptyState
          icon={<ClientsIcon />}
          title="Müvekkil yok"
          description={
            uygulananlar > 0
              ? "Bu süzgece uyan müvekkil bulunmuyor."
              : "Henüz bir müvekkil kaydı oluşturulmadı."
          }
        />
      ) : (
        /* Satırlar tek yüzeyde; gerekçe `TemplatesPage` ile aynı (kart başına satır değil). */
        <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-default bg-surface">
          {sorgu.data.data.items.map((musvekkil) => (
            <li
              key={musvekkil.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-4"
            >
              <Avatar name={musvekkil.displayName} size="md" />
              <span className="flex min-w-0 flex-col">
                <Link
                  className="truncate font-medium text-fg-heading hover:text-primary hover:underline"
                  to={`/app/muvekkiller/${musvekkil.id}`}
                >
                  {musvekkil.displayName}
                </Link>
                {/* İletişim bilgisi ZORUNLU DEĞİL (`[115]`); yokluğu bir hata değil. */}
                <span className="truncate text-xs text-fg-soft">{musvekkil.email ?? "—"}</span>
              </span>
              <Badge tone="neutral">{CLIENT_TYPE_NAMES[musvekkil.clientType]}</Badge>
              {musvekkil.status === ARSIV && (
                <Badge tone="neutral">{CLIENT_STATUS_NAMES.archived}</Badge>
              )}
              <span className="ml-auto text-xs text-fg-soft">
                Ekleyen: {musvekkil.createdByName}
              </span>
            </li>
          ))}
        </ul>
      )}

      {toplam > LIMIT && (
        <nav aria-label="Sayfalama" className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => suzgecYaz({ page: String(page - 1) })}
          >
            Önceki
          </Button>
          <span className="text-sm text-fg-soft">
            {page} / {sonSayfa}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={page >= sonSayfa}
            onClick={() => suzgecYaz({ page: String(page + 1) })}
          >
            Sonraki
          </Button>
        </nav>
      )}

      <p className="text-xs text-fg-muted">Toplam {toplam} müvekkil.</p>
      <ClientCreateModal open={olusturAcik} onClose={() => setOlusturAcik(false)} />
    </div>
  );
}
